import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons'
import { Page, Text, View } from '@react-pdf/renderer'
import { Button, Form, Input, message, Space } from 'antd'
import GeckoLogo from 'features/reporting/pdfGeneration/components/shared/GeckoLogo'
import { PageFooter } from 'features/reporting/pdfGeneration/components/shared/PageFooter'
import { PDFStyles } from 'features/reporting/pdfGeneration/styles/PDFStyles'
import { PdfProps, ReportSection } from 'features/reporting/types'
import { SectionHeader } from 'pages/reporting/reports/externalVisual/components/VisualInspectionSectionHeader'
import { ReferenceDocumentsData } from 'pages/reporting/reports/externalVisual/VisualReportData'
import { useVisualReportOps } from 'pages/reporting/reports/externalVisual/VisualReportProvider'
import { StyledForm } from 'pages/reporting/shared/StyledComponents'
import React, { useCallback, useMemo } from 'react'

const requiredReferenceDocumentsFields = ['referenceDocuments']

const RenderPDF: React.FC<PdfProps> = ({ onRender }) => {
  const { formData } = useVisualReportOps()
  const data = useMemo(
    () => formData.referenceDocumentsData,
    [formData.referenceDocumentsData]
  )

  const isPrelim = useMemo(
    () =>
      !(
        formData.signoff?.reviewerSignoff && formData.signoff?.supervisorSignoff
      ),
    [formData.signoff?.reviewerSignoff, formData.signoff?.supervisorSignoff]
  )

  return (
    <Page
      size='A4'
      style={PDFStyles.page}
      id='reference-documents'
      wrap={false}>
      {/* Hidden text for page tracking */}
      <Text
        render={({ pageNumber }) => {
          onRender({ id: 'reference-documents', pageNumber })
          return ''
        }}
      />

      <View style={PDFStyles.content}>
        {/* Header with Gecko logo */}
        <View style={PDFStyles.coverHeader}>
          <GeckoLogo />
          <View style={PDFStyles.coverHeaderContent}>
            <Text style={PDFStyles.coverHeaderText}>
              Checklist Inspection Report
            </Text>
          </View>
        </View>

        {/* Section Header */}
        <SectionHeader title='Reference Documents' />

        {/* Content */}
        <View style={{ marginTop: 10 }}>
          {data.referenceDocuments && data.referenceDocuments.length > 0 ? (
            <>
              {/* Table Header */}
              <View
                style={{
                  flexDirection: 'row',
                  backgroundColor: '#E5E7EB',
                  borderBottom: '1 solid #9CA3AF',
                  padding: 8,
                }}>
                <Text style={{ fontSize: 10, fontWeight: 700, width: '15%' }}>
                  Reference Document ID
                </Text>
                <Text style={{ fontSize: 10, fontWeight: 700, width: '40%' }}>
                  Description
                </Text>
                <Text style={{ fontSize: 10, fontWeight: 700, width: '45%' }}>
                  Document Path
                </Text>
              </View>

              {/* Table Rows */}
              {data.referenceDocuments.map((doc, index) => (
                <View
                  key={index}
                  style={{
                    flexDirection: 'row',
                    borderBottom: '1 solid #E5E7EB',
                    padding: 8,
                    backgroundColor: index % 2 === 0 ? 'white' : '#F9FAFB',
                  }}>
                  <Text style={{ fontSize: 9, width: '15%' }}>
                    ({index + 1})
                  </Text>
                  <Text style={{ fontSize: 9, width: '40%' }}>
                    ({index + 1}) {doc.description}
                  </Text>
                  <Text style={{ fontSize: 9, width: '45%' }}>{doc.path}</Text>
                </View>
              ))}
            </>
          ) : (
            <Text style={{ fontSize: 10 }}>No data to display.</Text>
          )}
        </View>
      </View>

      <PageFooter
        revisionNumber={formData.revisionMetadata.revisionNumber}
        prelim={isPrelim}
      />
    </Page>
  )
}

const RenderReact: React.FC = () => {
  const [form] = Form.useForm<ReferenceDocumentsData>()
  const { formData, handleReferenceDocumentsFinish } = useVisualReportOps()

  const onFinish = useCallback(
    async (values: ReferenceDocumentsData) => {
      try {
        await handleReferenceDocumentsFinish(values.referenceDocuments || [])
        message.success('Reference documents saved successfully!')
      } catch {
        message.error('Failed to save reference documents')
      }
    },
    [handleReferenceDocumentsFinish]
  )

  const initialValues = React.useMemo(
    () => formData.referenceDocumentsData,
    [formData.referenceDocumentsData]
  )

  return (
    <StyledForm<ReferenceDocumentsData>
      form={form}
      name='referenceDocumentsForm'
      onFinish={onFinish}
      layout='vertical'
      initialValues={initialValues}>
      <Form.List name='referenceDocuments'>
        {(fields, { add, remove }) => (
          <>
            {fields.map(({ key, name, ...restField }, index) => (
              <Space
                key={key}
                style={{
                  display: 'flex',
                  marginBottom: 8,
                  border: '1px solid #d9d9d9',
                  padding: 16,
                  borderRadius: 8,
                }}
                align='baseline'>
                <Form.Item label={`Document ${index + 1}`}>
                  <span>({index + 1})</span>
                </Form.Item>

                <Form.Item
                  {...restField}
                  name={[name, 'description']}
                  label='Description'
                  rules={[{ required: true, message: 'Missing description' }]}>
                  <Input
                    placeholder='513-116-010 4th floor DC-036'
                    style={{ width: 300 }}
                  />
                </Form.Item>

                <Form.Item
                  {...restField}
                  name={[name, 'path']}
                  label='Document Path'
                  rules={[
                    { required: true, message: 'Missing document path' },
                  ]}>
                  <Input
                    placeholder='513-116-010 4th floor DC-036.jpg'
                    style={{ width: 300 }}
                  />
                </Form.Item>

                <Form.Item {...restField} name={[name, 'id']} hidden>
                  <Input />
                </Form.Item>

                <MinusCircleOutlined onClick={() => remove(name)} />
              </Space>
            ))}

            <Form.Item>
              <Button
                type='dashed'
                onClick={() => add({ id: Date.now().toString() })}
                block
                icon={<PlusOutlined />}>
                Add Reference Document
              </Button>
            </Form.Item>
          </>
        )}
      </Form.List>

      <Form.Item>
        <Button type='primary' htmlType='submit'>
          Save Reference Documents
        </Button>
      </Form.Item>
    </StyledForm>
  )
}

export const ReferenceDocumentsSection: ReportSection = {
  RenderReact,
  required: false,
  RenderPDF,
  id: 'referenceDocuments',
  description: 'Supporting documentation',
  title: 'Reference Documents',
  requiredFields: requiredReferenceDocumentsFields,
}

export const ReferenceDocumentsSectionPDF = RenderPDF
